import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { LogOut, Trash2, Plus, X, Loader2, Wrench, Gauge, Inbox, Package, HelpCircle, BookOpen, Boxes, DollarSign, FileDown, Hammer, ListChecks } from "lucide-react";
import { AdminCrud } from "@/components/AdminCrud";

const PRODUCT_CFG = {
  key: "products", singular: "Product", listEndpoint: "/products/all", writeEndpoint: "/products",
  primary: ["name"], secondary: (i) => `$${Number(i.price).toFixed(2)}${i.category ? " · " + i.category : ""}${i.active === false ? " · HIDDEN" : ""}`,
  fields: [
    { key: "name", label: "Name", type: "text", placeholder: "Stage 2 ECU Tune" },
    { key: "description", label: "Description", type: "textarea", placeholder: "What's included…" },
    { key: "price", label: "Price (USD)", type: "number", placeholder: "899" },
    { key: "category", label: "Category", type: "text", placeholder: "Tuning" },
    { key: "image_url", label: "Image URL", type: "text", placeholder: "https://…" },
    { key: "active", label: "Active (visible in store)", type: "checkbox" },
  ],
};

const FAQ_CFG = {
  key: "faqs", singular: "FAQ", listEndpoint: "/faqs", writeEndpoint: "/faqs",
  primary: ["question"], secondary: (i) => i.answer,
  fields: [
    { key: "question", label: "Question", type: "text", placeholder: "Do you tune remotely?" },
    { key: "answer", label: "Answer", type: "textarea", placeholder: "Yes, we…" },
    { key: "order", label: "Order", type: "number", placeholder: "1" },
  ],
};

const RESOURCE_CFG = {
  key: "resources", singular: "Resource", listEndpoint: "/resources", writeEndpoint: "/resources",
  primary: ["title"], secondary: (i) => i.description,
  fields: [
    { key: "title", label: "Title", type: "text", placeholder: "E85 Flex Fuel Explained" },
    { key: "description", label: "Description", type: "textarea", placeholder: "Short summary…" },
    { key: "link", label: "Link (optional)", type: "text", placeholder: "https://…" },
    { key: "order", label: "Order", type: "number", placeholder: "1" },
  ],
};

const TURN14_CFG = {
  key: "turn14", singular: "Turn14 Brand", listEndpoint: "/store/turn14-brands", writeEndpoint: "/store/turn14-brands",
  primary: ["name"], secondary: (i) => `Brand ID: ${i.brand_id}`,
  fields: [
    { key: "name", label: "Brand Name", type: "text", placeholder: "Radium Engineering" },
    { key: "brand_id", label: "Turn14 Brand ID", type: "text", placeholder: "148" },
  ],
};

const TUNING_CFG = {
  key: "tuning-options", singular: "Tuning Option", listEndpoint: "/tuning-options", writeEndpoint: "/tuning-options",
  primary: ["label"],
  secondary: (i) => `${i.category === "base" ? "Base tune" : "Add-on"} · ${Number(i.price) === 0 ? "Included" : "$" + Number(i.price).toFixed(2)}${i.active === false ? " · HIDDEN" : ""}`,
  fields: [
    { key: "label", label: "Label", type: "text", placeholder: "Flex fuel (E85)" },
    { key: "category", label: "Category (base or addon)", type: "text", placeholder: "addon" },
    { key: "price", label: "Price (USD) — 0 = included", type: "number", placeholder: "250" },
    { key: "description", label: "Description", type: "textarea", placeholder: "Short description shown on the form…" },
    { key: "order", label: "Order", type: "number", placeholder: "1" },
    { key: "active", label: "Active (visible on estimator)", type: "checkbox" },
  ],
};

const STATUSES = ["new", "reviewing", "quoted", "closed"];
const statusColor = {
  new: "bg-neon text-void",
  reviewing: "bg-accent-purple text-[#FAFAFA]",
  quoted: "bg-[#333] text-[#FAFAFA]",
  closed: "bg-[#1A1A1A] text-[#9CA3AF]",
};

const emptyVehicle = { make: "", model: "", year_range: "", platform: "", power_gain: "", notes: "", image_url: "" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showVForm, setShowVForm] = useState(false);
  const [vForm, setVForm] = useState(emptyVehicle);
  const [editId, setEditId] = useState(null);
  const [savingV, setSavingV] = useState(false);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([api.get("/requests"), api.get("/vehicles")]);
      setRequests(r.data);
      setVehicles(v.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
    if (user) load();
  }, [user, navigate, load]);

  const changeStatus = async (id, status) => {
    const note = (notes[id] || "").trim();
    try {
      await api.put(`/requests/${id}/status`, { status, note });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      setNotes((prev) => ({ ...prev, [id]: "" }));
      toast.success(note ? `Status set to "${status}" — email sent with your note` : `Status set to "${status}" — customer emailed`);
    } catch {
      toast.error("Update failed");
    }
  };

  const delRequest = async (id) => {
    try {
      await api.delete(`/requests/${id}`);
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Request deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const downloadBuildsheet = async (r) => {
    try {
      const res = await api.get(`/requests/${r.id}/buildsheet`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vice-buildsheet-${(r.name || "request").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not generate build sheet");
    }
  };

  const openNewVehicle = () => { setVForm(emptyVehicle); setEditId(null); setShowVForm(true); };
  const openEditVehicle = (v) => { setVForm(v); setEditId(v.id); setShowVForm(true); };

  const saveVehicle = async (e) => {
    e.preventDefault();
    if (!vForm.make || !vForm.model) { toast.error("Make and model are required"); return; }
    setSavingV(true);
    try {
      const payload = { make: vForm.make, model: vForm.model, year_range: vForm.year_range, platform: vForm.platform, power_gain: vForm.power_gain, notes: vForm.notes, image_url: vForm.image_url };
      if (editId) {
        const { data } = await api.put(`/vehicles/${editId}`, payload);
        setVehicles((prev) => prev.map((v) => (v.id === editId ? data : v)));
      } else {
        const { data } = await api.post("/vehicles", payload);
        setVehicles((prev) => [data, ...prev]);
      }
      setShowVForm(false);
      toast.success("Vehicle saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingV(false);
    }
  };

  const delVehicle = async (id) => {
    try {
      await api.delete(`/vehicles/${id}`);
      setVehicles((prev) => prev.filter((v) => v.id !== id));
      toast.success("Vehicle deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!user) return <div className="min-h-screen bg-void flex items-center justify-center"><Loader2 className="animate-spin text-neon" /></div>;

  const newCount = requests.filter((r) => r.status === "new").length;

  return (
    <div className="min-h-screen bg-void" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-[#222] sticky top-0 z-40 bg-void">
        <div className="px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/vp-logo-neon.png" alt="Vice Performance" className="h-8 w-auto" />
            <span className="font-display uppercase text-xl tracking-tight">Vice <span className="text-neon">Command</span></span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-mono text-xs text-[#9CA3AF]">{user.email}</span>
            <button data-testid="logout-btn" onClick={() => logout().then(() => navigate("/admin/login"))} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#9CA3AF] hover:text-neon transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
        <div className="px-5 md:px-8 flex gap-6 border-t border-[#222]">
          {[["requests", "Requests", Inbox], ["vehicles", "Vehicles", Gauge], ["products", "Products", Package], ["turn14", "Turn14", Boxes], ["tuning-options", "Tuning", DollarSign], ["faqs", "FAQ", HelpCircle], ["resources", "Resources", BookOpen]].map(([key, label, Icon]) => (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setTab(key)}
              className={`py-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === key ? "border-neon text-neon" : "border-transparent text-[#9CA3AF] hover:text-[#FAFAFA]"}`}
            >
              <Icon size={15} /> {label}
              {key === "requests" && newCount > 0 && <span className="bg-neon text-void text-[10px] font-bold px-1.5">{newCount}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="px-5 md:px-8 py-8">
        {loading ? (
          <div className="flex items-center gap-2 font-mono text-sm text-[#9CA3AF]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        ) : tab === "requests" ? (
          <div className="space-y-4" data-testid="requests-panel">
            {requests.length === 0 ? (
              <div className="font-mono text-[#9CA3AF] border border-[#222] p-12 text-center">No requests yet.</div>
            ) : (
              requests.map((r) => (
                <div key={r.id} data-testid={`request-row-${r.id}`} className="border border-[#222] bg-matte">
                  <div className="p-5 flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${r.type === "fabrication" ? "bg-accent-purple text-[#FAFAFA]" : r.type === "build" ? "bg-[#500270] text-neon" : "bg-panel text-neon"}`}>
                          {r.type === "fabrication" ? <Wrench size={11} /> : r.type === "build" ? <Hammer size={11} /> : <Gauge size={11} />}{r.type}
                        </span>
                        <span className="font-grotesk font-bold text-lg text-[#FAFAFA]">{r.name}</span>
                        <span className="font-mono text-xs text-[#9CA3AF]">{new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 font-mono text-xs">
                        <div><span className="text-[#555]">EMAIL</span><br /><a href={`mailto:${r.email}`} className="text-[#FAFAFA] hover:text-neon break-all">{r.email}</a></div>
                        <div><span className="text-[#555]">PHONE</span><br /><span className="text-[#FAFAFA]">{r.phone || "—"}</span></div>
                        <div><span className="text-[#555]">VEHICLE</span><br /><span className="text-[#FAFAFA]">{[r.vehicle_year, r.vehicle_make, r.vehicle_model].filter(Boolean).join(" ") || "—"}</span></div>
                        <div><span className="text-[#555]">EST. QUOTE</span><br /><span className="text-neon font-bold">{r.estimate_total != null ? `$${Number(r.estimate_total).toLocaleString()}` : "—"}</span></div>
                      </div>
                      {r.tuning_selections?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`request-selections-${r.id}`}>
                          {r.tuning_selections.map((s, idx) => (
                            <span key={idx} className="text-[10px] font-mono uppercase tracking-wide border border-[#333] text-[#9CA3AF] px-2 py-0.5">
                              {s.label}{Number(s.price) > 0 ? ` +$${Number(s.price).toLocaleString()}` : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      {r.build_spec?.length > 0 && (
                        <div className="mt-3 border border-[#222] bg-void p-3" data-testid={`request-buildspec-${r.id}`}>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-neon mb-2">Engine Build Spec</div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 font-mono text-xs">
                            {r.build_spec.map((s, idx) => (
                              <div key={idx} className="flex justify-between gap-3 border-b border-[#161616] py-0.5">
                                <span className="text-[#666]">{s.label}</span>
                                <span className="text-[#FAFAFA] text-right">{s.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {r.type === "build" && r.engine_type && (
                        <div className="mt-3 font-mono text-xs" data-testid={`request-engine-${r.id}`}>
                          <span className="text-[#555]">ENGINE</span>{" "}
                          <span className="text-neon font-bold uppercase">{r.engine_type}</span>
                          {r.engine_type === "swapped" && r.swap_details && <span className="text-[#FAFAFA]"> — {r.swap_details}</span>}
                        </div>
                      )}
                      {r.build_checklist?.length > 0 && (
                        <div className="mt-3 border border-[#222] bg-void p-3" data-testid={`request-checklist-${r.id}`}>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-neon mb-2 flex items-center gap-1.5"><ListChecks size={12} /> Work Checklist</div>
                          <div className="space-y-1 font-mono text-xs">
                            {r.build_checklist.map((s, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <span className={`w-3 h-3 border flex items-center justify-center shrink-0 ${s.checked ? "border-neon bg-neon" : "border-[#555]"}`}>
                                  {s.checked && <span className="text-void text-[8px] leading-none">✓</span>}
                                </span>
                                <span className={s.checked ? "text-[#FAFAFA]" : "text-[#666] line-through"}>{s.label}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="mt-3 font-mono text-sm text-[#CFCFCF] whitespace-pre-wrap border-l-2 border-neon pl-3">{r.description}</p>
                      {r.attachments?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {r.attachments.map((p) => (
                            <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" className="block w-20 h-20 border border-[#222] overflow-hidden hover:border-neon">
                              <img src={fileUrl(p)} alt="attachment" className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex lg:flex-col items-stretch lg:items-end gap-2 shrink-0 lg:w-56">
                      <div className="w-full">
                        <input
                          data-testid={`status-note-${r.id}`}
                          value={notes[r.id] || ""}
                          onChange={(e) => setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))}
                          placeholder="Optional note for customer…"
                          className="w-full bg-void border border-[#222] px-3 py-2 font-mono text-xs text-[#FAFAFA] placeholder:text-[#555] outline-none focus:border-neon rounded-none"
                        />
                        <span className="block text-[9px] uppercase tracking-[0.15em] text-[#555] mt-1 lg:text-right">Included in the status email</span>
                      </div>
                      <select
                        data-testid={`status-select-${r.id}`}
                        value={r.status}
                        onChange={(e) => changeStatus(r.id, e.target.value)}
                        className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1.5 outline-none cursor-pointer border-none rounded-none ${statusColor[r.status] || ""}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-void text-[#FAFAFA]">{s}</option>)}
                      </select>
                      <button data-testid={`buildsheet-${r.id}`} onClick={() => downloadBuildsheet(r)} title="Download build sheet PDF" className="border border-[#333] text-[10px] uppercase tracking-widest px-3 py-1.5 hover:border-neon hover:text-neon transition-colors flex items-center gap-1.5">
                        <FileDown size={13} /> Build Sheet
                      </button>
                      <button data-testid={`delete-request-${r.id}`} onClick={() => delRequest(r.id)} className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : tab === "vehicles" ? (
          <div data-testid="vehicles-panel">
            <div className="flex justify-between items-center mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#9CA3AF]">{vehicles.length} platforms</span>
              <button data-testid="add-vehicle-btn" onClick={openNewVehicle} className="flex items-center gap-2 bg-neon text-void font-bold uppercase tracking-[0.15em] text-xs py-3 px-5 hover:bg-[#FAFAFA] transition-colors">
                <Plus size={16} /> Add Vehicle
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((v) => (
                <div key={v.id} data-testid={`admin-vehicle-${v.id}`} className="border border-[#222] bg-matte">
                  {v.image_url && <div className="aspect-video overflow-hidden border-b border-[#222]"><img src={v.image_url} alt={v.model} className="w-full h-full object-cover grayscale" /></div>}
                  <div className="p-4">
                    <div className="font-display uppercase text-xl tracking-tight">{v.make} {v.model}</div>
                    <div className="mt-1 font-mono text-xs text-[#9CA3AF]">{[v.year_range, v.platform, v.power_gain].filter(Boolean).join(" · ")}</div>
                    <div className="mt-4 flex gap-2">
                      <button data-testid={`edit-vehicle-${v.id}`} onClick={() => openEditVehicle(v)} className="flex-1 border border-[#333] text-xs uppercase tracking-widest py-2 hover:border-neon hover:text-neon transition-colors">Edit</button>
                      <button data-testid={`delete-vehicle-${v.id}`} onClick={() => delVehicle(v.id)} className="border border-[#333] text-[#9CA3AF] hover:text-red-400 hover:border-red-900 transition-colors px-3"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : tab === "products" ? (
          <AdminCrud config={PRODUCT_CFG} />
        ) : tab === "turn14" ? (
          <AdminCrud config={TURN14_CFG} />
        ) : tab === "tuning-options" ? (
          <AdminCrud config={TUNING_CFG} />
        ) : tab === "faqs" ? (
          <AdminCrud config={FAQ_CFG} />
        ) : (
          <AdminCrud config={RESOURCE_CFG} />
        )}
      </main>

      {/* Vehicle form modal */}
      {showVForm && (
        <div className="fixed inset-0 z-50 bg-void/80 flex items-center justify-center p-4" onClick={() => setShowVForm(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={saveVehicle}
            data-testid="vehicle-form"
            className="w-full max-w-lg bg-matte border border-[#222] max-h-[90vh] overflow-y-auto"
          >
            <div className="p-5 border-b border-[#222] flex items-center justify-between">
              <span className="font-display uppercase text-2xl tracking-tight">{editId ? "Edit Vehicle" : "Add Vehicle"}</span>
              <button type="button" onClick={() => setShowVForm(false)}><X className="text-[#9CA3AF] hover:text-neon" /></button>
            </div>
            <div className="p-5 space-y-4">
              {[
                ["make", "Make *", "Nissan"],
                ["model", "Model *", "GT-R"],
                ["year_range", "Year Range", "R35 / 2009+"],
                ["platform", "Platform / Engine", "VR38DETT"],
                ["power_gain", "Power Gain", "+350 WHP"],
                ["image_url", "Image URL", "https://…"],
              ].map(([k, label, ph]) => (
                <label key={k} className="block">
                  <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-1">{label}</span>
                  <input data-testid={`vform-${k}`} value={vForm[k]} onChange={(e) => setVForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} className="w-full bg-void border border-[#222] px-3 py-2 font-mono text-sm outline-none focus:border-neon focus:ring-1 focus:ring-neon rounded-none" />
                </label>
              ))}
              <label className="block">
                <span className="block text-xs uppercase tracking-[0.2em] text-[#9CA3AF] mb-1">Notes</span>
                <textarea data-testid="vform-notes" rows={3} value={vForm.notes} onChange={(e) => setVForm((f) => ({ ...f, notes: e.target.value }))} className="w-full bg-void border border-[#222] px-3 py-2 font-mono text-sm outline-none focus:border-neon focus:ring-1 focus:ring-neon rounded-none" />
              </label>
            </div>
            <div className="p-5 border-t border-[#222]">
              <button data-testid="save-vehicle-btn" disabled={savingV} className="w-full bg-neon text-void font-bold uppercase tracking-[0.2em] text-sm py-3 flex items-center justify-center gap-2 hover:bg-[#FAFAFA] transition-colors disabled:opacity-50">
                {savingV ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Save Vehicle"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
